import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  InputBase,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Chat } from './BottomDrawer';
import { Socket } from 'socket.io-client';

type PropTypes = {
  chat: Chat[];
  chatMembers?: string[];
  focusInput?: number;
  gameId: string;
  isDrawerOpen: boolean;
  isTeamPickingChat?: boolean;
  openedBottomDrawerByClick: boolean;
  small?: boolean;
  socket: Socket;
  spectate?: boolean;
  title?: string;
  username: string;
};

export const BottomChatWindow = ({
  chat,
  chatMembers,
  focusInput,
  gameId,
  isDrawerOpen,
  isTeamPickingChat,
  openedBottomDrawerByClick,
  small,
  socket,
  spectate,
  title,
  username,
}: PropTypes) => {
  const theme = useTheme();
  const inputBaseRef = useRef<any>(null);
  const boxRef = useRef<any>(null);
  const allowSendChatRef = useRef(2);

  useEffect(() => {
    const useSocket = socket;
    const keyDownHandler = (event: any) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (allowSendChatRef.current < 2) {
          allowSendChatRef.current = 2;
          return;
        }
        if (!inputBaseRef.current?.value) return;
        useSocket.emit('chat', {
          gameId,
          username,
          text: inputBaseRef.current.value,
          leftChat: false,
          teamPickingChat: isTeamPickingChat,
        });
        inputBaseRef.current.value = '';
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
    // since pressing 'Enter' will open the drawer from close
    // and if text exists in the chat input box, we don't want to send that text at the same time
    // as opening the drawer, this is another hacky way to prevent that behavior with a tri-state
    if (openedBottomDrawerByClick) {
      allowSendChatRef.current = 2;
      return;
    }
    if (!isDrawerOpen) {
      allowSendChatRef.current = 0;
    } else {
      if (inputBaseRef.current?.value) {
        allowSendChatRef.current = 1;
      } else {
        allowSendChatRef.current = 2;
      }
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!openedBottomDrawerByClick) return;
    allowSendChatRef.current = 2;
    inputBaseRef.current.focus();
  }, [openedBottomDrawerByClick]);

  useEffect(() => {
    scrollToBottomOfChat();
  }, [chat]);

  useEffect(() => {
    if (focusInput === undefined) return;
    inputBaseRef.current.focus();
  }, [focusInput]);

  const handleChatBoxClick = () => {
    inputBaseRef.current.focus();
  };

  const handleInput = () => {
    scrollToBottomOfChat();
  };

  const scrollToBottomOfChat = () => {
    const scrollHeight = boxRef.current.scrollHeight;
    boxRef.current.scrollTop = scrollHeight;
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Typography
        variant="caption"
        textAlign={'center'}
        letterSpacing={2}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: 'black',
          fontWeight: 600,
        }}
      >
        {title || 'POST GAME CHAT'}
      </Typography>
      <Stack direction={'row'} sx={{ height: '100%' }}>
        <Stack sx={{ width: '75%', marginY: 0.2, marginRight: 0.2 }}>
          <Box
            ref={boxRef}
            sx={{
              paddingY: 0.5,
              height: small ? 120 : 240,
              backgroundColor: '#2e3033',
              overflow: 'auto',
            }}
            onClick={handleChatBoxClick}
          >
            {chat
              ?.filter((c) => !c.leftChat)
              .map((c, i) => (
                <Stack
                  key={i}
                  direction={'row'}
                  alignItems={'center'}
                  sx={{ position: 'relative' }}
                >
                  <Typography
                    variant="subtitle1"
                    ml={2}
                    color="primary.main"
                    textAlign={'left'}
                    width={'100%'}
                    lineHeight={1.5}
                  >
                    <span
                      style={{
                        marginRight: 10,
                        textAlign: 'left',
                        color: 'white',
                        minWidth: '50px',
                        maxWidth: '80px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'pre',
                      }}
                    >
                      {c.username}:
                    </span>
                    {c.text}
                  </Typography>
                </Stack>
              ))}
          </Box>
          <Stack
            sx={{
              height: 35,
              paddingY: 0.2,
              paddingX: 2,
              backgroundColor: '#162024',
            }}
          >
            <InputBase
              multiline
              maxRows={4}
              type="text"
              inputRef={inputBaseRef}
              onInput={handleInput}
              sx={{
                '.css-156xqnd-MuiInputBase-input': { padding: 0 },
              }}
              fullWidth
              placeholder="Type something..."
            />
          </Stack>
        </Stack>
        <Stack sx={{ width: '25%', marginY: 0.2 }}>
          <Box
            sx={{
              height: small ? 155 : 275,
              backgroundColor: '#3a3a3a',
            }}
          >
            <Stack>
              <Typography
                variant="subtitle2"
                sx={{
                  paddingY: 0.2,
                  paddingX: 1,
                  fontWeight: 600,
                }}
              >
                {chatMembers?.length} Member
                {chatMembers?.length === 1 ? '' : 's'}
              </Typography>
              <Divider />
              <Box
                sx={{
                  height: small ? 125 : 250,
                  overflow: 'auto',
                }}
              >
                <Stack
                  sx={{
                    paddingY: 0.2,
                    paddingX: 1,
                  }}
                >
                  {chatMembers?.map((cm) => (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'pre',
                      }}
                    >
                      {cm}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
